import React, { useEffect, useState, useRef, useCallback } from 'react';
import { message, Popover } from 'antd';
import { getBoundingRect, getRectangle, getPageFromElement } from '../../lib/pdfjs-dom';
import { Label } from '../../types/label';
import { Position } from '../../types/result';
import { getScaled } from '../../lib/coordinates';
import { NewLabelPopup } from '../NewLabelPopup';
import { PDFViewer } from 'pdfjs-dist/web/pdf_viewer';
import useGlobal from '../../store/Store';

type Coordinates = { x: number; y: number };
type Rectangle = { x1: number; y1: number; x2: number; y2: number };
type Selection = {
  page: { node: HTMLElement; number: number };
  position: Rectangle;
  reset: () => void;
};

type Props = {
  container: null | HTMLElement;
  viewer: PDFViewer;
  shouldStart: () => boolean;
  setSelection: (selected: boolean) => any;
  onAddLabel: (position: Position, page: { node: HTMLElement; number: number }, label: string) => any;
  labelEdit: null | Label;
  onEditLabel: (position: Position, page: { node: HTMLElement; number: number }) => any;
  onRemoveLabel: (label: Label) => any;
  onReEditLabel: (label: Label) => any;
  onResetRemovedLabel: (label: Label) => any;
  labels: any[];
  allowResize: boolean;
};

export const MouseSelection = ({
  container,
  viewer,
  shouldStart,
  setSelection,
  onAddLabel,
  labelEdit,
  onEditLabel,
  onRemoveLabel,
  onReEditLabel,
  onResetRemovedLabel,
  labels,
  allowResize,
}: Props) => {
  const [globalState, globalActions] = useGlobal();
  const [locked, setLocked] = useState<boolean>(false);
  const [startTarget, setStartTarget] = useState<null | HTMLElement>(null);
  const [start, setStart] = useState<null | Coordinates>(null);
  const [end, setEnd] = useState<null | Coordinates>(null);
  const [currentSelection, setCurrentSelection] = useState<null | Selection>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  const containerNode = useRef<null | HTMLDivElement>(null);
  const { current: popoverContainer } = containerNode;

  useEffect(() => {
    if (container) {
      container.addEventListener('mousemove', mouseMove);
      container.addEventListener('mousedown', mouseDown);
      container.addEventListener('mouseup', mouseUp);
    }
    document.addEventListener('keydown', onKeyDown);

    return () => {
      if (container) {
        container.removeEventListener('mousemove', mouseMove);
        container.removeEventListener('mousedown', mouseDown);
        container.removeEventListener('mouseup', mouseUp);
      }
      document.removeEventListener('keydown', onKeyDown);
    };
  });

  useEffect(() => {
    if (containerNode.current) {
      setIsReady(true);
    }
  }, [containerNode]);

  const containerCoordinates = (container: HTMLElement, pageX: number, pageY: number) => {
    const containerBoundingRect = container.getBoundingClientRect();

    return {
      x: pageX - (containerBoundingRect.left + window.scrollX) + container.scrollLeft,
      y: pageY - (containerBoundingRect.top + window.scrollY) + container.scrollTop,
    };
  };

  const mouseMove = (event: MouseEvent) => {
    if (!start || locked || !container) {
      return;
    }

    setEnd(containerCoordinates(container, event.pageX, event.pageY));
  };

  const mouseDown = (event: MouseEvent) => {
    if (allowResize) {
      return;
    }

    if (!shouldStart() || !container) {
      return;
    }

    // prevents selection outside page
    const page = getPageFromElement(event.target as HTMLElement);
    if (!page) {
      return;
    }

    // prevents selection from resetting when adding highlight
    const eventTarget: null | HTMLElement = event.target as HTMLElement;
    if (eventTarget && eventTarget.closest('.ant-popover')) {
      return;
    }

    var coor = containerCoordinates(container, event.pageX, event.pageY);

    setLocked(false);
    setEnd(null);
    setStart(coor);
    setStartTarget(eventTarget);
  };

  const mouseUp = (event: MouseEvent) => {
    // prevents selection from moving when adding highlight
    if (!container) return;
    const eventTarget: null | HTMLElement = event.target as HTMLElement;
    if (eventTarget && eventTarget.closest('.ant-popover')) {
      return;
    }

    if (!start || !startTarget) {
      return;
    }

    const page = getPageFromElement(startTarget);
    if (!page) {
      return;
    }

    const end = containerCoordinates(container, event.pageX, event.pageY);
    const rectangle = getRectangle(start, end);
    const position = getScaled(viewer, page, rectangle);

    if (!shouldRender(rectangle)) {
      reset();
      return;
    }
    setEnd(end);
    setLocked(true);
    setCurrentSelection({ page, position, reset });
    setSelection(true);
  };

  const reset = useCallback(() => {
    setLocked(false);
    setStart(null);
    setEnd(null);
    setStartTarget(null);
    setCurrentSelection(null);
    setSelection(false);
  }, [setSelection]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      reset();
    }
  };

  const shouldRender = (rectangle: Rectangle) => {
    const width = Math.abs(rectangle.x2 - rectangle.x1);
    const height = Math.abs(rectangle.y2 - rectangle.y1);
    return width >= 1 && height >= 1;
  };

  const addLabel = useCallback(
    async (label: undefined | string) => {
      if (!label || !currentSelection) return;

      const { position, page } = currentSelection;
      const newLabel = await onAddLabel(position, page, label);
      globalActions.addToLabelHistory(newLabel);
      reset();
    },
    [currentSelection, onAddLabel, reset, globalActions]
  );

  const editLabel = () => {
    if (!currentSelection) return;

    const oldLabel = globalState.labelEdit;
    const { position, page } = currentSelection;

    onEditLabel(position, page);

    if (oldLabel) globalActions.addToLabelHistory(oldLabel);
    reset();

    if (globalState.labelEditCopied) {
      globalActions.setLabelEditCopied(false);
    }
  };

  const undo = useCallback(() => {
    const labelHistory = globalState.labelHistory;
    const lastAddedLabel = labelHistory[labelHistory.length - 1];
    if (lastAddedLabel) {
      const compareLabel = globalState.labels.find(l => l.id === lastAddedLabel.id);
      if (!compareLabel) {
        onResetRemovedLabel(lastAddedLabel);
      } else if (lastAddedLabel.word === compareLabel?.word) {
        onRemoveLabel(lastAddedLabel);
      } else {
        onReEditLabel(lastAddedLabel);
      }
      globalActions.removeFromLabelHistory();
    }
  }, [onRemoveLabel, onReEditLabel, onResetRemovedLabel, globalState, globalActions]);

  const keyPress = useCallback(
    (event: any): void => {
      if (event.keyCode === 90 && (event.ctrlKey || event.metaKey)) {
        if (globalState.labelHistory.length) undo();
      } else {
        if (!currentSelection) return;
        const match = globalState.labelOptions?.find(
          label => label.shortcut?.toLowerCase() === event.key.toLowerCase()
        );
        if (match) {
          addLabel(match.name);
        } else {
          message.info(`There is no label matching the "${event.key}" shortcut.`);
        }
      }
    },
    [globalState.labelOptions, globalState.labelHistory, addLabel, currentSelection, undo]
  );

  useEffect(() => {
    window.addEventListener('keydown', keyPress);
    return () => {
      window.removeEventListener('keydown', keyPress);
    };
  }, [keyPress]);

  const getPopoverContent = () => {
    if (labelEdit) {
      editLabel();
    } else {
      return <NewLabelPopup onSubmit={addLabel} labels={globalState.labelOptions} keyPress={keyPress} />;
    }
  };

  return (
    <div className="MouseSelection-container" ref={containerNode}>
      {popoverContainer && isReady && start && end && (
        <Popover
          visible={locked}
          content={getPopoverContent()}
          getPopupContainer={() => popoverContainer}
          title={labelEdit ? labelEdit.name : 'Add label'}
          overlayStyle={{ zIndex: 10 }}>
          <div className="MouseSelection" style={getBoundingRect(start, end)} />
        </Popover>
      )}
    </div>
  );
};
